import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate, type Location } from 'react-router-dom';
import { Icon } from './ui';

interface SheetState {
  title: string;
  body: ReactNode;
  /** Вызывается при закрытии (например, отмена потока черновика). */
  onClose?: () => void;
  /** Почти во весь экран — просмотр документа. */
  wide?: boolean;
}

interface MenuState {
  anchor: DOMRect;
  body: ReactNode;
}

interface UI {
  toast(msg: string): void;
  openSheet(s: SheetState): void;
  closeSheet(): void;
  sheetOpen: boolean;
  openMenu(anchor: HTMLElement, body: ReactNode): void;
  closeMenu(): void;
  openApp(id: string): void;
  closePanel(): void;
  sideOpen: boolean;
  setSideOpen(v: boolean): void;
}

const Ctx = createContext<UI | null>(null);

export function useUI(): UI {
  const u = useContext(Ctx);
  if (!u) throw new Error('useUI вне UIProvider');
  return u;
}

/** Фоновый экран, поверх которого открыта панель карточки (`/applications/:id`). */
export function backgroundOf(loc: Location): Location | null {
  return (loc.state as { background?: Location } | null)?.background ?? null;
}

export function UIProvider({ children }: { children: ReactNode }) {
  const [toastMsg, setToast] = useState('');
  const [sheet, setSheet] = useState<SheetState | null>(null);
  const [menu, setMenu] = useState<MenuState | null>(null);
  const [sideOpen, setSideOpen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const nav = useNavigate();
  const loc = useLocation();
  const locRef = useRef(loc);
  locRef.current = loc;
  const sheetRef = useRef(sheet);
  sheetRef.current = sheet;

  const toast = useCallback((msg: string) => {
    setToast(msg);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(''), 3200);
  }, []);

  const closeSheet = useCallback(() => {
    sheetRef.current?.onClose?.();
    setSheet(null);
  }, []);

  const openApp = useCallback(
    (id: string) => {
      const cur = locRef.current;
      const background = backgroundOf(cur) ?? (cur.pathname.startsWith('/applications/') ? null : cur);
      if (sheetRef.current) closeSheet();
      setMenu(null);
      setSideOpen(false);
      nav(`/applications/${id}`, { state: background ? { background } : undefined });
    },
    [nav, closeSheet],
  );

  const closePanel = useCallback(() => {
    const bg = backgroundOf(locRef.current);
    if (bg) nav(`${bg.pathname}${bg.search}`);
    else nav('/applications');
  }, [nav]);

  const value = useMemo<UI>(
    () => ({
      toast,
      openSheet: (s) => {
        setMenu(null);
        setSheet(s);
      },
      closeSheet,
      sheetOpen: !!sheet,
      openMenu: (anchor, body) => setMenu({ anchor: anchor.getBoundingClientRect(), body }),
      closeMenu: () => setMenu(null),
      openApp,
      closePanel,
      sideOpen,
      setSideOpen,
    }),
    [toast, closeSheet, sheet, openApp, closePanel, sideOpen],
  );

  useEffect(() => {
    document.body.style.overflow = sheet ? 'hidden' : '';
  }, [sheet]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (sheetRef.current) closeSheet();
      else if (menu) setMenu(null);
      else if (sideOpen) setSideOpen(false);
      else if (locRef.current.pathname.match(/^\/applications\/[^/]+$/)) closePanel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menu, sideOpen, closeSheet, closePanel]);

  return (
    <Ctx.Provider value={value}>
      {children}
      {sheet && (
        <Sheet title={sheet.title} onClose={closeSheet} wide={sheet.wide}>
          {sheet.body}
        </Sheet>
      )}
      {menu && (
        <PopMenu anchor={menu.anchor} onClose={() => setMenu(null)}>
          {menu.body}
        </PopMenu>
      )}
      <div className="toast" role="status" hidden={!toastMsg}>
        {toastMsg}
      </div>
    </Ctx.Provider>
  );
}

function Sheet({
  title,
  onClose,
  children,
  wide,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const first = ref.current?.querySelector<HTMLElement>(
      'input:not([type=hidden]),textarea,select,button:not(.icon-btn)',
    );
    (first ?? ref.current)?.focus();
    return () => prev?.focus?.();
  }, []);
  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className={`sheet${wide ? ' wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={ref}
        tabIndex={-1}
      >
        <div className="sh-head">
          <h2>{title}</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Закрыть">
            <Icon name="x" size={20} />
          </button>
        </div>
        <div className="sh-body">{children}</div>
      </div>
    </div>
  );
}

function PopMenu({
  anchor,
  onClose,
  children,
}: {
  anchor: DOMRect;
  onClose: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: 0, top: 0 });
  useLayoutEffect(() => {
    const w = 230;
    const h = ref.current?.offsetHeight ?? 0;
    let top = anchor.bottom + 4;
    if (top + h > window.innerHeight - 8) top = Math.max(8, anchor.top - h - 4);
    setPos({ left: Math.max(8, Math.min(anchor.right - w, window.innerWidth - w - 8)), top });
  }, [anchor]);
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    const t = setTimeout(() => document.addEventListener('mousedown', onDown), 0);
    ref.current?.querySelector<HTMLElement>('button')?.focus();
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', onDown);
    };
  }, [onClose]);
  return (
    <div
      id="menu"
      role="menu"
      ref={ref}
      style={pos}
      onClick={(e) => (e.target as HTMLElement).closest('button') && onClose()}
    >
      {children}
    </div>
  );
}

/** Кнопка с подтверждением вторым нажатием (как в прототипе: «Нажмите ещё раз»). */
export function ConfirmButton({
  className,
  onConfirm,
  children,
  confirmText = 'Нажмите ещё раз',
}: {
  className?: string;
  onConfirm: () => void;
  children: ReactNode;
  confirmText?: string;
}) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(t);
  }, [armed]);
  return (
    <button
      type="button"
      className={className}
      onClick={() => {
        if (armed) {
          setArmed(false);
          onConfirm();
        } else setArmed(true);
      }}
    >
      {armed ? confirmText : children}
    </button>
  );
}
